import { useEffect, useMemo, useState } from 'react';
import { useFeature } from '../../lib/features';
import { track } from '../../lib/analytics';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Copy, Loader2, Lock, MessageCircle, NotebookText, Share2, Users } from 'lucide-react';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { PublicSiteHeader } from '../../components/PublicSiteHeader';
import { PublicSiteFooter } from '../../components/PublicSiteFooter';
import { BunchedMessageGroup } from '../../components/signal/BunchedMessageGroup';
import { PoweredByZmistBadge } from '../../components/ui/PoweredByZmistBadge';
import { PublicReportBody } from '../../components/reports/PublicReportBody';
import { SignalActivityChart } from '../../components/charts/SignalActivityChart';
import { bunchMessages } from '../../lib/bunchMessages';
import {
  fetchChannel,
  fetchMyChannelStats,
  fetchPublicChannel,
  fetchPublicReport,
  fetchPublicReportHistory,
  fetchPublicSignals,
  fetchTemplates,
  followChannel,
  unfollowChannel,
  type FetchedSignal,
  type PublicChannelView,
  type PublicReport,
} from '../../api/dataApi';
import { rememberPostLoginRedirect, useStore } from '../../store/StoreContext';
import { REPORT_TYPE, type ChartVariant, type Message, type Template } from '../../types';

const VALID_CHART_VARIANTS: ChartVariant[] = ['number', 'bar', 'line', 'circle'];
function asChartVariant(value: string): ChartVariant {
  return (VALID_CHART_VARIANTS as string[]).includes(value) ? (value as ChartVariant) : 'bar';
}

// A thin row divider between list items inside the big white content card
// below (see the return statement) - lighter than a bordered sub-card now
// that every item sits on a shared white surface instead of its own.
const ROW_DIVIDER = 'border-black/[0.06] dark:border-white/[0.08]';

// ---------------------------------------------------------------------
// SEO: this is still a client-rendered SPA route (no SSR/prerendering),
// so index.html's generic <title>/description are what crawlers and
// social-share unfurlers that don't execute JS (Slack, Twitter/X,
// iMessage, most chat-app link previews) actually see - this effect only
// helps crawlers that do run JS (Googlebot's second wave) plus the
// browser tab/history entry itself. For a guaranteed-correct social
// preview and search snippet per channel, landing/'s static
// /channels/<slug>/ page (see landing/templates.mjs's renderChannelPage)
// is still the real SEO surface - this is a best-effort companion for
// the live app route, not a replacement for it.
const SEO_STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'is', 'are', 'was', 'were',
  'with', 'this', 'that', 'these', 'those', 'it', 'its', 'at', 'by', 'as', 'be', 'from', 'but',
  'not', 'all', 'any', 'can', 'will', 'has', 'have', 'had', 'you', 'your', 'we', 'our',
]);

// Pulls a handful of distinctive words out of a channel's own title/
// description/tags for the <meta name="keywords"> tag - Google itself
// ignores that tag, but it's cheap, harmless, and some secondary search
// engines/directories still read it, so it's free SEO surface area as
// long as it's drawn from the channel's real content rather than stuffed.
function extractKeywords(parts: string[], max = 12): string {
  const seen = new Set<string>();
  const out: string[] = [];
  const addWord = (w: string) => {
    if (w.length > 2 && !SEO_STOPWORDS.has(w) && !seen.has(w)) {
      seen.add(w);
      out.push(w);
    }
  };
  for (const part of parts) {
    for (const raw of part.toLowerCase().split(/[^a-z0-9]+/)) {
      if (out.length >= max) return out.join(', ');
      if (raw) addWord(raw);
    }
  }
  return out.join(', ');
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

// Upserts a <meta>/<link> tag and hands back a restore function that puts
// the <head> back exactly how it found it (removes a tag this effect
// created, or restores a prior tag's original attribute value) - so
// navigating off this screen to any other route never leaves another
// public channel's title/description/OG tags behind.
function upsertMeta(attr: 'name' | 'property', key: string, content: string): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (existing) {
    const prev = existing.getAttribute('content');
    existing.setAttribute('content', content);
    return () => {
      if (prev === null) existing.removeAttribute('content');
      else existing.setAttribute('content', prev);
    };
  }
  const el = document.createElement('meta');
  el.setAttribute(attr, key);
  el.setAttribute('content', content);
  document.head.appendChild(el);
  return () => el.remove();
}

function upsertCanonical(href: string): () => void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    const prev = existing.getAttribute('href');
    existing.setAttribute('href', href);
    return () => {
      if (prev === null) existing.removeAttribute('href');
      else existing.setAttribute('href', prev);
    };
  }
  const el = document.createElement('link');
  el.setAttribute('rel', 'canonical');
  el.setAttribute('href', href);
  document.head.appendChild(el);
  return () => el.remove();
}

// Sets document.title + description/keywords/canonical/OG/Twitter tags
// from the channel's own title/description/tags while this screen is
// mounted, and restores whatever was there before on unmount.
function usePublicChannelSeo(view: PublicChannelView | null) {
  useEffect(() => {
    if (!view) return;

    const description = truncate(
      view.description || `Live signal data from ${view.title}, shared publicly on Zmist.`,
      160
    );
    const title = truncate(`${view.title} — Public Channel | Zmist`, 70);
    const keywords = extractKeywords([view.title, ...view.tags, view.description, 'public channel', 'live data', 'Zmist']);
    const url = `${window.location.origin}/public/${encodeURIComponent(view.nickname)}`;
    const image = `${window.location.origin}/android-chrome-512x512.png`;

    const prevTitle = document.title;
    document.title = title;

    const restores = [
      upsertMeta('name', 'description', description),
      upsertMeta('name', 'keywords', keywords),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', 'Zmist'),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:image', image),
      upsertMeta('name', 'twitter:card', 'summary'),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', image),
      upsertCanonical(url),
    ];

    return () => {
      document.title = prevTitle;
      restores.forEach((restore) => restore());
    };
  }, [view]);
}

// GET /public/channels/{nickname} - the live-showcase homepage's canonical
// public channel page: no share-link token, no password, reachable for
// any channel that's just plain isPublic (see backend/workflows/channel/
// public_view.wsl's header comment on why this is distinct from
// /shared/:nickname/:token, which needs an owner-enabled share link).
// Deliberately a separate, smaller screen from PublicChannelViewScreen.tsx
// rather than a generalized mode of it - that screen's token/password/
// join-request/clone state is all share-link-specific and none of it
// applies here; duplicating the read-only render (messages/charts/reports
// tabs, signal medallions) was the lower-risk option versus threading an
// "optional token" through every one of that screen's existing calls.
export function PublicChannelPageScreen() {
  const cloneOn = useFeature('channels.clone');
  const { nickname } = useParams();
  const { isLoggedIn, cloneChannel } = useStore();
  const navigate = useNavigate();

  const [view, setView] = useState<PublicChannelView | null>(null);
  const [report, setReport] = useState<PublicReport | null>(null);
  const [messages, setMessages] = useState<FetchedSignal[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [rawDataOpen, setRawDataOpen] = useState(false);
  const [chartType, setChartType] = useState<ChartVariant>('bar');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [reportHistory, setReportHistory] = useState<PublicReport[] | null>(null);
  const [reportHistoryLoading, setReportHistoryLoading] = useState(false);

  usePublicChannelSeo(view);

  useEffect(() => {
    if (view) track('public_channel_viewed', { origin_channel_id: view.nickname });
  }, [view]);

  useEffect(() => {
    if (!nickname) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [channelView, latestReport] = await Promise.all([fetchPublicChannel(nickname), fetchPublicReport(nickname)]);
        if (cancelled) return;
        setView(channelView);
        setReport(latestReport);
        setChartType(asChartVariant(channelView.viewChartType));
        if (isLoggedIn) {
          fetchMyChannelStats(nickname)
            .then((s) => !cancelled && setFollowing(s.following))
            .catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load this channel.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname]);

  useEffect(() => {
    if (!nickname || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      // Tokenless GET /public/channels/{nickname}/signals: no session, share
      // token or CSRF anywhere on this path (limit is capped at 50 server-side).
      const PAGE_SIZE = 50;
      const PUBLIC_VIEW_MAX_PAGES = 100;
      let offset = 0;
      let all: FetchedSignal[] = [];
      for (let page = 0; page < PUBLIC_VIEW_MAX_PAGES; page++) {
        const signalsPage = await fetchPublicSignals(nickname, offset, PAGE_SIZE);
        if (cancelled) return;
        all = all.concat(signalsPage.signals);
        if (!signalsPage.hasMore) break;
        offset += signalsPage.limit || PAGE_SIZE;
      }
      const templatesPage = await fetchTemplates();
      if (cancelled) return;
      setMessages(all.sort((a, b) => (a.date < b.date ? 1 : -1)));
      setTemplates(templatesPage?.templates ?? []);
    })()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nickname, messages.length]);

  const chartMessages: Message[] = useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        sender: m.sender,
        text: m.text,
        date: m.date,
        isMine: false,
        type: !m.type || m.type === 'message' ? undefined : m.type,
        value: m.value,
        at: m.at,
        signalNumber: m.signalNumber,
        labels: m.labels,
        calc: m.calc,
      })),
    [messages]
  );

  // Reports are their own tab (renderReport, fed by `report`/fetchPublicReport
  // above) - the Messages tab is for signals/chat only, so a type:"report"
  // entry that used to come in with the signal feed (reports are posted as a regular
  // type:"report" signal under the hood - see internal/report.PostReport)
  // would otherwise show up twice: once here, once in Reports.
  const nonReportMessages = useMemo(() => messages.filter((m) => m.type !== REPORT_TYPE), [messages]);

  // Message bunching (same channel-owner setting as the private chat's
  // Message bunching form field - see src/lib/bunchMessages.ts's header
  // comment): consecutive same-sender messages within view.bunchingInterval
  // seconds of each other render as one grouped block instead of a row
  // each. 0/missing (e.g. before the channel's first save with this field)
  // behaves as "off" - one group per message.
  const messageGroups = useMemo(
    () => bunchMessages(nonReportMessages, view?.bunchingInterval ?? 0),
    [nonReportMessages, view?.bunchingInterval]
  );

  const goToAuth = (path: '/login' | '/register') => {
    rememberPostLoginRedirect(`/public/${nickname}`);
    navigate(path);
  };

  const toggleFollow = async () => {
    if (!nickname) return;
    if (!isLoggedIn) {
      goToAuth('/login');
      return;
    }
    setFollowBusy(true);
    if (!following) track('follow_clicked', { origin_channel_id: nickname });
    try {
      const result = following ? await unfollowChannel(nickname) : await followChannel(nickname);
      setFollowing(result.following);
      if (result.following) track('follow_completed', { origin_channel_id: nickname });
    } catch {
      /* a failed follow/unfollow just leaves the button in its prior state */
    } finally {
      setFollowBusy(false);
    }
  };

  const share = async () => {
    if (!nickname) return;
    const url = `${window.location.origin}/public/${nickname}`;
    track('channel_shared', { origin_channel_id: nickname });
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      /* clipboard access denied - the URL is still visible in the address bar */
    }
  };

  const createSimilar = async () => {
    track('create_similar_clicked', { origin_channel_id: nickname });
    if (!isLoggedIn) {
      goToAuth('/register');
      return;
    }
    if (!nickname) return;
    setCloning(true);
    setCloneError('');
    try {
      const full = await fetchChannel(nickname);
      if (!full) throw new Error('Unable to load this channel to clone.');
      const created = await cloneChannel(full, 'public_channel_page');
      navigate(`/channels/${created.id}/edit`);
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Unable to create a similar channel.');
    } finally {
      setCloning(false);
    }
  };

  function renderMessagesList() {
    if (contentLoading) {
      return (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Loader2 size={28} className="animate-spin text-black/40 dark:text-white/40" />
          <span className="text-sm text-black/50 dark:text-white/50">Loading raw data…</span>
        </div>
      );
    }
    if (nonReportMessages.length === 0) {
      return <EmptyState icon={MessageCircle} title="No raw data yet" subtitle="This channel has no activity yet." />;
    }
    return messageGroups.map((group, i) => (
      <div key={group[0].id} className={i > 0 ? `border-t ${ROW_DIVIDER}` : ''}>
        <BunchedMessageGroup group={group} templates={templates} dividerClassName={ROW_DIVIDER} />
      </div>
    ));
  }

  const toggleRawData = () => setRawDataOpen((open) => !open);

  const toggleReportHistory = async () => {
    if (reportHistory !== null) {
      setReportHistory(null);
      return;
    }
    if (!nickname) return;
    setReportHistoryLoading(true);
    try {
      const { reports } = await fetchPublicReportHistory(nickname);
      setReportHistory(reports);
    } finally {
      setReportHistoryLoading(false);
    }
  };

  function renderReport() {
    if (!report) {
      return <EmptyState icon={NotebookText} title="No reports" subtitle="This channel has no reports yet." />;
    }
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {report.number > 0 && (
            <span className="text-xs font-semibold text-black/60 dark:text-white/60">
              Report #{report.number}
              {report.rangeStart && report.rangeEnd && (
                <span className="font-normal text-black/40 dark:text-white/40">
                  {' '}
                  · Data captured: {new Date(report.rangeStart).toLocaleDateString()} –{' '}
                  {new Date(report.rangeEnd).toLocaleDateString()}
                </span>
              )}
            </span>
          )}
          {report.number > 0 && (
            <button
              type="button"
              onClick={toggleReportHistory}
              className="text-xs font-semibold text-(--color-accent) hover:underline"
            >
              {reportHistory !== null ? 'Hide report history' : 'View report history'}
            </button>
          )}
        </div>

        {reportHistory !== null && (
          <div className="flex flex-col rounded-xl border border-black/10 dark:border-white/10 divide-y divide-black/[0.06] dark:divide-white/[0.08]">
            {reportHistoryLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={18} className="animate-spin text-black/40 dark:text-white/40" />
              </div>
            ) : (
              reportHistory.map((r) => (
                <Link
                  key={r.id}
                  to={`/public/${nickname}/reports/${r.number}`}
                  className="px-3 py-2 text-sm hover:bg-black/[0.03] dark:hover:bg-white/[0.05] flex items-center justify-between gap-2"
                >
                  <span className="font-medium">Report #{r.number}</span>
                  <span className="text-xs text-black/40 dark:text-white/40">
                    {r.date ? new Date(r.date).toLocaleDateString() : ''}
                  </span>
                </Link>
              ))
            )}
          </div>
        )}

        <PublicReportBody report={report} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-bg) text-(--color-text) flex flex-col">
      <PublicSiteHeader />
      <div className="flex-1 w-full flex flex-col items-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-[1000px] flex flex-col gap-5">
          {loading && !view && <span className="text-sm text-black/50 dark:text-white/50 text-center">Loading...</span>}

          {!loading && error && !view && <EmptyState icon={Lock} title="This channel isn't available" subtitle={error} />}

          {view && (
            <>
              <div className="flex flex-col items-center text-center gap-2">
                <AvatarView symbol={view.avatarSymbol} colorHex={view.accentHex} imageKey={view.avatarImageKey} size={64} zoomable />
                <h1 className="text-2xl font-bold m-0">{view.title}</h1>
                {view.description && <p className="text-sm text-black/50 dark:text-white/50 m-0">{view.description}</p>}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                  <span className="inline-flex items-center rounded-full border border-(--color-accent) text-(--color-accent) text-xs px-3 py-1">
                    Public channel
                  </span>
                  <span className="inline-flex items-center rounded-full bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-xs px-3 py-1">
                    {view.nickname}
                  </span>
                </div>
                {!view.badgeRemovable && <PoweredByZmistBadge />}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                  <Button variant="bordered" onClick={toggleFollow} loading={followBusy} disabled={followBusy}>
                    <Users size={16} />
                    {following ? 'Following' : 'Follow'}
                  </Button>
                  <Button variant="bordered" onClick={share}>
                    <Share2 size={16} />
                    {shareCopied ? 'Link copied' : 'Share'}
                  </Button>
                  {cloneOn && (
                    <Button variant="bordered" onClick={createSimilar} loading={cloning} disabled={cloning}>
                      <Copy size={16} />
                      Create similar channel
                    </Button>
                  )}
                </div>
                {cloneError && <span className="text-sm text-red-500">{cloneError}</span>}
              </div>

              <div className="w-full flex flex-col rounded-[20px] bg-white dark:bg-white/5 border border-black/10 dark:border-white/10 shadow-sm">
                <div className="px-5 pt-5 pb-5">{renderReport()}</div>

                <div className={`px-5 pt-4 pb-5 border-t ${ROW_DIVIDER}`}>
                  <h3 className="text-sm font-semibold text-black/60 dark:text-white/60 mb-3">Charts</h3>
                  {contentLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 size={24} className="animate-spin text-black/30 dark:text-white/30" />
                    </div>
                  ) : (
                    <SignalActivityChart
                      messages={chartMessages}
                      templates={templates}
                      chartType={chartType}
                      onChartTypeChange={setChartType}
                      locked={!view.publicChartFiltersAllowed}
                      initialSettings={view.chartSettings}
                      storageKey={`chart.public.${nickname}`}
                    />
                  )}
                </div>

                <div className={`border-t ${ROW_DIVIDER}`}>
                  <button
                    type="button"
                    onClick={toggleRawData}
                    className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-black/60 dark:text-white/60">Raw Data</span>
                    {rawDataOpen ? (
                      <ChevronUp size={16} className="text-black/30 dark:text-white/30" />
                    ) : (
                      <ChevronDown size={16} className="text-black/30 dark:text-white/30" />
                    )}
                  </button>
                  {rawDataOpen && <div className="px-5 pb-5">{renderMessagesList()}</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <PublicSiteFooter />
    </div>
  );
}
